import { useMemo } from 'react';

import { getExternalJwtToken } from '../api';
import { decodeJwtToken } from '../jwt';

/**
 * useJwtAuth
 * A custom hook that handles JWT authentication logic
 *
 * @returns {object} - Returns an object with the following properties:
 * - `isAuthenticated` - Boolean value indicating if the user is authenticated via JWT
 * - `token` - The JWT token string (or null if not present)
 * - `decodedToken` - The decoded JWT token payload (or null if invalid)
 * - `isExpired` - Boolean value indicating if the token is expired
 */
export const useJwtAuth = () => {
  const token = getExternalJwtToken();

  const jwtAuthState = useMemo(() => {
    if (!token || token.trim() === '') {
      return {
        isAuthenticated: false,
        token: null,
        decodedToken: null,
        isExpired: false,
      };
    }

    try {
      const decoded = decodeJwtToken(token);
      if (!decoded) {
        return {
          isAuthenticated: false,
          token,
          decodedToken: null,
          isExpired: true,
        };
      }

      // Check if token has expiration
      let isExpired = false;
      if (decoded.exp) {
        const currentTime = Math.floor(Date.now() / 1000);
        isExpired = decoded.exp <= currentTime;
      }

      return {
        isAuthenticated: !isExpired,
        token,
        decodedToken: decoded,
        isExpired,
      };
    } catch (error) {
      return {
        isAuthenticated: false,
        token,
        decodedToken: null,
        isExpired: true,
      };
    }
  }, [token]);

  return jwtAuthState;
};
